import React, { useState, useEffect, useMemo } from "react";
import {
  Paper,
  Text,
  Stack,
  Button,
  Group,
  LoadingOverlay,
  Tooltip,
  ActionIcon,
} from "@mantine/core";
import { IconPlus, IconInfoCircle, IconArrowsSort } from "@tabler/icons-react";

import { StaffFormModal, upsertStaffMember } from "../forms/StaffForm";
import StaffCard from "./StaffCard";
import StaffReorderPanel from "./StaffReorderPanel";
import { SettingsPageLayout, SettingsPageHeader, SettingsTabs } from "./common";
import "./StaffManagementSettings.css";
import { useSalon } from "../../context/SalonContext";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import usePermissions from "../../hooks/usePermissions";
import {
  filterStaffByScope,
  getEffectiveScope,
} from "../../utils/permissionFilters";

function StaffManagement() {
  const {
    businessInfo,
    employees,
    setEmployees,
    refreshEmployees,
    currentUser,
  } = useSalon();
  const { refreshAccess } = useFeatureAccess();
  const perms = usePermissions();
  const staffPerms = perms?.settingsStaff;
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState("active");
  const [isReordering, setIsReordering] = useState(false);

  // Get staff scope for UI filtering
  const staffScope = getEffectiveScope(staffPerms?.scope);
  const currentUserId = currentUser?.id || "";

  useEffect(() => {
    if (businessInfo && employees && currentUser) {
      setIsLoading(false);
    }
  }, [businessInfo, employees, currentUser]);

  // Filter employees by scope permission (UI-only filtering)
  const scopeFilteredEmployees = useMemo(() => {
    return filterStaffByScope(employees, {
      currentUserId,
      scope: staffScope,
    });
  }, [employees, currentUserId, staffScope]);

  // Filter employees by status (from scope-filtered list)
  const activeEmployees = useMemo(() => {
    return (
      scopeFilteredEmployees?.filter(
        (emp) => emp.status === "Active" || emp.status === undefined,
      ) || []
    );
  }, [scopeFilteredEmployees]);

  const inactiveEmployees = useMemo(() => {
    return (
      scopeFilteredEmployees?.filter((emp) => emp.status === "Inactive") || []
    );
  }, [scopeFilteredEmployees]);

  const displayedEmployees =
    activeTab === "active" ? activeEmployees : inactiveEmployees;

  const staffTabs = [
    { value: "active", label: `Active Staff (${activeEmployees.length})` },
    {
      value: "inactive",
      label: `Inactive Staff (${inactiveEmployees.length})`,
    },
  ];

  // Invite or edit: merge the saved staff member into the list right away, so
  // a new member shows even if a later step of the save (services) fails.
  const handleStaffSaved = (savedEmployee) => {
    setEmployees((current) => upsertStaffMember(current, savedEmployee));
  };

  const handleEditClick = (e, employee) => {
    setSelectedEmployee(employee);
    setIsEditModalOpen(true);
  };

  const closeStaffModals = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
  };

  const handleStaffSaveComplete = async () => {
    closeStaffModals();
    try {
      // Refresh access so subscription/feature counts (e.g. number_of_active_employees) update
      await refreshAccess();
    } catch (error) {
      console.error("Error refreshing access:", error);
    }
  };

  // After a successful reorder, re-fetch the roster (the backend now returns
  // staff sorted by the new order) so both this list and the calendar reflect
  // it, then exit reorder mode.
  const handleReorderSaved = async () => {
    await refreshEmployees();
    setIsReordering(false);
  };

  return (
    <>
      <LoadingOverlay visible={isLoading} />
      <SettingsPageLayout
        header={
          <SettingsPageHeader
            title="Staff"
            subtitle="People with access to your studio"
            actions={
              !isReordering && (
                <Group gap="xs" wrap="nowrap">
                  {staffScope === "own" && (
                    <Tooltip
                      label="You can only view and edit your own profile. Contact a manager to view other staff members."
                      multiline
                      w={280}
                      withArrow
                    >
                      <ActionIcon
                        variant="transparent"
                        color="gray"
                        aria-label="Staff visibility is limited to your own profile"
                      >
                        <IconInfoCircle size={20} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {staffPerms?.canEdit !== false && (
                    <Button
                      variant="default"
                      leftSection={<IconArrowsSort size={16} />}
                      onClick={() => setIsReordering(true)}
                      disabled={activeEmployees.length < 2}
                    >
                      Edit Order
                    </Button>
                  )}
                  {staffPerms?.canEdit !== false && (
                    <Button
                      leftSection={<IconPlus size="1rem" />}
                      color="var(--brand-teal)"
                      onClick={() => setIsAddModalOpen(true)}
                    >
                      Add Staff
                    </Button>
                  )}
                </Group>
              )
            }
          />
        }
      >
        {isReordering ? (
          <StaffReorderPanel
            employees={activeEmployees}
            salonId={businessInfo?.id}
            onCancel={() => setIsReordering(false)}
            onSaved={handleReorderSaved}
          />
        ) : (
          <>
            <SettingsTabs
              items={staffTabs}
              value={activeTab}
              onChange={setActiveTab}
              ariaLabel="Staff status"
            />

            <Stack gap="xs">
              {displayedEmployees.length === 0 ? (
                <Paper withBorder p="xl" radius="md">
                  <Text ta="center" c="dimmed">
                    {activeTab === "active"
                      ? "No active staff members"
                      : "No inactive staff members"}
                  </Text>
                </Paper>
              ) : (
                displayedEmployees.map((employee) => (
                  <StaffCard
                    key={employee.id}
                    employee={employee}
                    onEdit={handleEditClick}
                    currentUser={currentUser}
                    canEdit={staffPerms?.canEdit !== false}
                  />
                ))
              )}
            </Stack>
          </>
        )}
      </SettingsPageLayout>

      <StaffFormModal
        opened={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Staff Member"
        onStaffSaved={handleStaffSaved}
        onComplete={handleStaffSaveComplete}
      />

      <StaffFormModal
        opened={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Staff Member"
        initialData={selectedEmployee}
        onStaffSaved={handleStaffSaved}
        onComplete={handleStaffSaveComplete}
      />
    </>
  );
}

export default StaffManagement;
